/**
 * haloSnapshot — build a shareable, zero-PII PNG summarising the
 * user's crypto hygiene. Drawn entirely client-side on a 1200×630
 * canvas (Twitter/LinkedIn social-card proportions) so nothing
 * ever leaves the device beyond the image the user explicitly
 * shares.
 *
 * Privacy posture:
 *   - NO usernames, NO user id, NO peer handles.
 *   - Counters only (Sent/Worked/Pending) + rotation-rhythm
 *     sparkline of the CALLER's own timestamps. Someone else
 *     holding this image learns nothing except "this person
 *     rotates keys on a regular cadence and has nudged N contacts
 *     to do the same".
 *   - Watermark ties the image back to the product.
 */

const W = 1200;
const H = 630;
const BG = '#0b1220';
const ACCENT = '#60a5fa';
const MUTED = '#475569';
const EMERALD = '#34d399';
const AMBER = '#fbbf24';
const VIOLET = '#c4b5fd';
const SLATE = '#e2e8f0';
const TEXT_DIM = '#94a3b8';

// Lightweight helper. Reuses 2d context throughout.
const _pickCtx = () => {
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext('2d');
  ctx.textBaseline = 'alphabetic';
  return { cv, ctx };
};

const _roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const _drawTile = (ctx, { x, y, w, h, label, value, valueColor, bg, border }) => {
  ctx.fillStyle = bg;
  _roundRect(ctx, x, y, w, h, 16);
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = 2;
  _roundRect(ctx, x, y, w, h, 16);
  ctx.stroke();

  ctx.fillStyle = TEXT_DIM;
  ctx.font = '600 20px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(label.toUpperCase(), x + w / 2, y + 46);

  ctx.fillStyle = valueColor;
  ctx.font = '800 84px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.fillText(String(value), x + w / 2, y + h - 44);
};

const _drawSparkline = (ctx, { x, y, w, h, timestamps }) => {
  // Axis background.
  ctx.fillStyle = '#111827';
  _roundRect(ctx, x, y, w, h, 12);
  ctx.fill();
  ctx.strokeStyle = '#1f2937';
  ctx.lineWidth = 1;
  _roundRect(ctx, x, y, w, h, 12);
  ctx.stroke();

  // Axis midline.
  const midY = y + h / 2;
  ctx.strokeStyle = '#1f2937';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 20, midY);
  ctx.lineTo(x + w - 20, midY);
  ctx.stroke();

  // 6 week markers.
  for (let i = 1; i < 6; i += 1) {
    const tx = x + 20 + (i / 6) * (w - 40);
    ctx.beginPath();
    ctx.moveTo(tx, midY - 6);
    ctx.lineTo(tx, midY + 6);
    ctx.stroke();
  }

  if (!timestamps || timestamps.length === 0) {
    ctx.fillStyle = TEXT_DIM;
    ctx.font = '500 18px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('No rotation history yet', x + w / 2, midY + 6);
    return;
  }

  // Dots over a 42-day window (6 weeks).
  const now = Date.now();
  const windowMs = 42 * 86400_000;
  const clampedTs = [...timestamps].sort((a, b) => a - b);
  const latest = clampedTs[clampedTs.length - 1];
  for (const ts of clampedTs) {
    const age = now - ts;
    const frac = Math.max(0, 1 - age / windowMs);
    const px = x + 20 + frac * (w - 40);
    const isLast = ts === latest;
    ctx.beginPath();
    ctx.arc(px, midY, isLast ? 7 : 5, 0, Math.PI * 2);
    ctx.fillStyle = isLast ? EMERALD : ACCENT;
    ctx.fill();
  }

  // Axis labels.
  ctx.fillStyle = TEXT_DIM;
  ctx.font = '500 16px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('-42d', x + 20, y + h - 12);
  ctx.textAlign = 'right';
  ctx.fillText('now', x + w - 20, y + h - 12);
};

const _drawShieldGlyph = (ctx, cx, cy, r) => {
  // Minimal shield outline. Not a logo asset — we avoid network
  // fetches so the image generates offline and on a flaky link.
  ctx.save();
  ctx.fillStyle = ACCENT;
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r, cy - r / 2);
  ctx.lineTo(cx + r, cy + r / 4);
  ctx.quadraticCurveTo(cx + r, cy + r, cx, cy + r);
  ctx.quadraticCurveTo(cx - r, cy + r, cx - r, cy + r / 4);
  ctx.lineTo(cx - r, cy - r / 2);
  ctx.closePath();
  ctx.fill();
  // Inner checkmark.
  ctx.strokeStyle = '#0b1220';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.45, cy + r * 0.05);
  ctx.lineTo(cx - r * 0.05, cy + r * 0.4);
  ctx.lineTo(cx + r * 0.5, cy - r * 0.25);
  ctx.stroke();
  ctx.restore();
};

/**
 * Main entry. Accepts counters + the timestamps of the caller's
 * OWN rotations (never a peer's — that'd be PII). Returns a PNG
 * blob ready to download, share, or clipboard-copy.
 */
export const generateHaloSnapshot = async ({
  sent = 0,
  worked = 0,
  pending = 0,
  sasVerifiedByPeers = 0,
  rotationTimestamps = [],
}) => {
  const { cv, ctx } = _pickCtx();

  // Background — solid dark panel + subtle grain via two overlay rects.
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  // Soft top-left accent glow.
  const g = ctx.createRadialGradient(W * 0.15, H * 0.15, 20, W * 0.15, H * 0.15, 600);
  g.addColorStop(0, 'rgba(96, 165, 250, 0.25)');
  g.addColorStop(1, 'rgba(96, 165, 250, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Shield glyph + wordmark top-left.
  _drawShieldGlyph(ctx, 68, 76, 30);
  ctx.fillStyle = SLATE;
  ctx.font = '700 32px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Government Shield', 116, 88);

  // Headline.
  ctx.fillStyle = SLATE;
  ctx.font = '800 52px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('My security halo', 68, 170);

  // Subtext.
  ctx.fillStyle = TEXT_DIM;
  ctx.font = '500 22px ui-sans-serif, system-ui, sans-serif';
  ctx.fillText(
    'Keys I rotate. Contacts I nudge. Cadence I hold.',
    68, 204
  );

  // Counter tiles — 4 across. Slightly smaller tile width vs the
  // original 3-tile layout so the "Voice verified" tile fits in
  // the same 630h canvas without crowding the rotation sparkline.
  const tilesY = 260;
  const tileH = 180;
  const gap = 20;
  const tileW = (W - 68 * 2 - gap * 3) / 4;

  _drawTile(ctx, {
    x: 68,                                  y: tilesY, w: tileW, h: tileH,
    label: 'Sent',                          value: sent,
    valueColor: SLATE,
    bg: 'rgba(148, 163, 184, 0.08)',        border: 'rgba(148, 163, 184, 0.25)',
  });
  _drawTile(ctx, {
    x: 68 + tileW + gap,                    y: tilesY, w: tileW, h: tileH,
    label: 'Worked',                        value: worked,
    valueColor: EMERALD,
    bg: 'rgba(52, 211, 153, 0.10)',         border: 'rgba(52, 211, 153, 0.35)',
  });
  _drawTile(ctx, {
    x: 68 + (tileW + gap) * 2,              y: tilesY, w: tileW, h: tileH,
    label: 'Pending',                       value: pending,
    valueColor: AMBER,
    bg: 'rgba(251, 191, 36, 0.10)',         border: 'rgba(251, 191, 36, 0.35)',
  });
  // Voice-verified tile — 4th pillar next to the nudge trio.
  // Counts distinct peers who've cryptographically SAS-attested
  // the user's voice identity in the last 90 days. Violet palette
  // matches the in-app Security Halo card so the snapshot reads
  // as a faithful export of what the user sees.
  _drawTile(ctx, {
    x: 68 + (tileW + gap) * 3,              y: tilesY, w: tileW, h: tileH,
    label: 'Voice verified',                value: sasVerifiedByPeers,
    valueColor: VIOLET,
    bg: 'rgba(196, 181, 253, 0.10)',        border: 'rgba(196, 181, 253, 0.35)',
  });

  // Rotation sparkline.
  const sparkY = tilesY + tileH + 32;
  ctx.fillStyle = TEXT_DIM;
  ctx.font = '600 18px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('MY ROTATION RHYTHM', 68, sparkY + 2);
  _drawSparkline(ctx, {
    x: 68, y: sparkY + 14, w: W - 68 * 2, h: 80, timestamps: rotationTimestamps,
  });

  // Watermark.
  ctx.fillStyle = MUTED;
  ctx.font = '500 18px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('government-shield.app', W - 68, H - 40);

  return new Promise((resolve) => {
    cv.toBlob((blob) => resolve(blob), 'image/png', 0.92);
  });
};
